/**
 * Baby mobs as their own models (26.1 "baby mob" redesign): each has its own
 * geometry and its own `*_baby` sheet instead of a scaled-down adult.
 *
 * Geometry is Mojang's Bedrock baby geometry (bedrock-samples
 * resource_pack/models/entity/baby_*.geo.json) kept in its native form —
 * y up, feet at y = 0, cube `o` = min corner, pivots absolute — and converted
 * to the renderer's vanilla-Java part space (y down, feet at y = 24) by
 * `buildGeo`. Box-UV unwrap is the same in both editions.
 */

import * as THREE from 'three';
import type { MobType, MobView } from '../../game/types';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat } from './core';
import type { QuadPose } from './mobs';
import { composedTexture, villagerLayers } from './villageModels';

const PI = Math.PI;
type V3 = readonly [number, number, number];

interface GeoCube {
  o: V3; s: V3; uv: readonly [number, number];
  inflate?: number; mirror?: boolean;
  /** Per-cube rotation (degrees) about its own pivot. */
  pivot?: V3; rot?: V3;
}
interface GeoBone { name: string; parent?: string; pivot: V3; cubes?: readonly GeoCube[] }
interface Geo { tw: number; th: number; bones: readonly GeoBone[] }

const ZOMBIE: Geo = {
  tw: 64, th: 64, bones: [
    { name: 'body', pivot: [0, 6.5, 0], cubes: [{ o: [-2, 4, -1], s: [4, 5, 2], uv: [16, 16] }] },
    { name: 'head', parent: 'body', pivot: [0, 8.75, 0], cubes: [
      { o: [-3, 9, -3], s: [6, 6, 6], uv: [3, 3] },
      { o: [-3, 8.9, -3], s: [6, 6, 6], uv: [35, 3], inflate: 0.25 },
    ] },
    { name: 'rightArm', parent: 'body', pivot: [-3, 8.5, 0], cubes: [{ o: [-4, 4, -1], s: [2, 5, 2], uv: [36, 16] }] },
    { name: 'leftArm', parent: 'body', pivot: [3, 8.5, 0], cubes: [{ o: [2, 4, -1], s: [2, 5, 2], uv: [28, 16] }] },
    { name: 'rightLeg', parent: 'body', pivot: [-1, 4, 0], cubes: [{ o: [-2, 0, -1], s: [2, 4, 2], uv: [8, 16] }] },
    { name: 'leftLeg', parent: 'body', pivot: [1, 4, 0], cubes: [{ o: [0, 0, -1], s: [2, 4, 2], uv: [0, 16] }] },
  ],
};

const PIG: Geo = {
  tw: 32, th: 32, bones: [
    { name: 'body', pivot: [0, 5, 0.5], cubes: [{ o: [-3.5, 2, -4], s: [7, 6, 9], uv: [0, 0] }] },
    { name: 'head', pivot: [0, 5, -2], cubes: [
      { o: [-3.5, 4, -7], s: [7, 6, 6], uv: [0, 15], inflate: 0.01 },
      { o: [-1.5, 5, -8], s: [3, 2, 1], uv: [6, 27] },
    ] },
    { name: 'leg0', pivot: [-2.5, 2, 4], cubes: [{ o: [-3.475, 0, 3], s: [2, 2, 2], uv: [23, 4] }] },
    { name: 'leg1', pivot: [2.5, 2, 4], cubes: [{ o: [1.475, 0, 3], s: [2, 2, 2], uv: [0, 4] }] },
    { name: 'leg2', pivot: [-2.5, 2, -3], cubes: [{ o: [-3.475, 0, -4], s: [2, 2, 2], uv: [23, 0] }] },
    { name: 'leg3', pivot: [2.5, 2, -3], cubes: [{ o: [1.475, 0, -4], s: [2, 2, 2], uv: [0, 0] }] },
  ],
};

const COW: Geo = {
  tw: 64, th: 64, bones: [
    { name: 'head', pivot: [0, 10.43096, -5.16667], cubes: [
      { o: [-3, 9, -10], s: [6, 6, 5], uv: [0, 18] },
      { o: [3, 14, -9], s: [1, 2, 1], uv: [8, 29] },
      { o: [-4, 14, -9], s: [1, 2, 1], uv: [4, 29], mirror: true },
      { o: [-2, 9, -11], s: [4, 3, 1], uv: [12, 29] },
    ] },
    { name: 'body', pivot: [0, 9, 0], cubes: [{ o: [-4, 6, -6], s: [8, 6, 12], uv: [0, 0] }] },
    { name: 'leg0', pivot: [-2.475, 6, 3.5], cubes: [{ o: [-3.975, 0, 2], s: [3, 6, 3], uv: [22, 27] }] },
    { name: 'leg1', pivot: [2.475, 6, 3.5], cubes: [{ o: [0.975, 0, 2], s: [3, 6, 3], uv: [34, 27] }] },
    { name: 'leg2', pivot: [-2.475, 6, -3.5], cubes: [{ o: [-3.975, 0, -5], s: [3, 6, 3], uv: [22, 18] }] },
    { name: 'leg3', pivot: [2.475, 6, -3.5], cubes: [{ o: [0.975, 0, -5], s: [3, 6, 3], uv: [34, 18] }] },
  ],
};

/** The Java sheet is 64x32 with the 32x32 layout in its left half. */
const SHEEP: Geo = {
  tw: 64, th: 32, bones: [
    { name: 'head', pivot: [0, 8.5, -2.5], cubes: [{ o: [-2.5, 8, -6], s: [5, 5, 5], uv: [0, 0] }] },
    { name: 'leg0', pivot: [-2, 5, 3], cubes: [{ o: [-2.975, 0, 2], s: [2, 5, 2], uv: [0, 23] }] },
    { name: 'leg1', pivot: [2, 5, 3], cubes: [{ o: [0.975, 0, 2], s: [2, 5, 2], uv: [24, 12] }] },
    { name: 'leg2', pivot: [-2, 5, -2], cubes: [{ o: [-2.975, 0, -3], s: [2, 5, 2], uv: [8, 23] }] },
    { name: 'leg3', pivot: [2, 5, -2], cubes: [{ o: [0.975, 0, -3], s: [2, 5, 2], uv: [24, 5] }] },
    { name: 'body', pivot: [0, 8, 2.5], cubes: [{ o: [-3, 5, -4], s: [6, 4, 9], uv: [0, 10] }] },
  ],
};

const CHICKEN: Geo = {
  tw: 16, th: 16, bones: [
    { name: 'body', pivot: [0, 3.75, -1.25], cubes: [
      { o: [-2, 2, -2], s: [4, 4, 4], uv: [0, 0] },
      { o: [-1, 3, -3], s: [2, 1, 1], uv: [10, 8] },
    ] },
    { name: 'leg0', pivot: [-1, 2, 0.5], cubes: [{ o: [-1.5, 0, 0.5], s: [1, 2, 0], uv: [0, 2] }, { o: [-1.5, 0, -0.5], s: [1, 0, 1], uv: [0, 0] }] },
    { name: 'leg1', pivot: [1, 2, 0.5], cubes: [{ o: [0.5, 0, 0.5], s: [1, 2, 0], uv: [2, 2] }, { o: [0.5, 0, -0.5], s: [1, 0, 1], uv: [0, 1] }] },
    { name: 'wing0', pivot: [-2, 4, 0], cubes: [{ o: [-3, 4, -1], s: [1, 0, 2], uv: [4, 8] }] },
    { name: 'wing1', pivot: [2, 4, 0], cubes: [{ o: [2, 4, -1], s: [1, 0, 2], uv: [6, 8] }] },
  ],
};

const VILLAGER_HEAD: readonly GeoBone[] = [
  { name: 'head', pivot: [0, 8, 0], cubes: [{ o: [-4, 8, -3.5], s: [8, 8, 7], uv: [0, 0] }] },
  { name: 'helmet', parent: 'head', pivot: [0, 12, 0], cubes: [{ o: [-4, 8, -3.5], s: [8, 8, 7], uv: [0, 30], inflate: 0.3 }] },
  { name: 'brim', parent: 'head', pivot: [0, 12.5, 0], cubes: [{ o: [-7, 12, -6], s: [14, 1, 12], uv: [0, 45] }] },
  { name: 'nose', parent: 'head', pivot: [0, 10, -4], cubes: [{ o: [-1, 8, -4.5], s: [2, 2, 1], uv: [23, 0] }] },
];

const VILLAGER: Geo = {
  tw: 64, th: 64, bones: [
    { name: 'body', pivot: [0, 5.25, 0], cubes: [
      { o: [-2, 3, -1.5], s: [4, 5, 3], uv: [0, 15] },
      { o: [-2, 2, -1.5], s: [4, 6, 3], uv: [16, 21], inflate: 0.2 },
    ] },
    ...VILLAGER_HEAD,
    { name: 'arms', pivot: [0, 6.5, 0], cubes: [
      { o: [-2, 4.59, -2.8], s: [4, 2, 2], uv: [24, 17], pivot: [0, 5.59757, -1.81746], rot: [-60, 0, 0] },
      { o: [2, 3.59, -2.8], s: [2, 4, 2], uv: [16, 15], pivot: [3, 5.09754, -0.95992], rot: [-60, 0, 0] },
      { o: [-4, 3.59, -2.8], s: [2, 4, 2], uv: [36, 15], pivot: [-3, 5.09754, -0.95992], rot: [-60, 0, 0] },
    ] },
    { name: 'rightLeg', pivot: [-1, 2.5, 0], cubes: [{ o: [-2, 0, -1], s: [2, 3, 2], uv: [8, 23] }] },
    { name: 'leftLeg', pivot: [1, 2.5, 0], cubes: [{ o: [0, 0, -1], s: [2, 3, 2], uv: [0, 23] }] },
  ],
};

/** The zombie villager's sheet puts its overlays one row lower and has separate arms. */
const ZOMBIE_VILLAGER: Geo = {
  tw: 64, th: 64, bones: [
    { name: 'body', pivot: [0, 5.25, 0], cubes: [
      { o: [-2, 3, -1.5], s: [4, 5, 3], uv: [0, 15] },
      { o: [-2, 2, -1.5], s: [4, 6, 3], uv: [16, 22], inflate: 0.2 },
    ] },
    { name: 'head', pivot: [0, 8, 0], cubes: [{ o: [-4, 8, -3.5], s: [8, 8, 7], uv: [0, 0] }] },
    { name: 'helmet', parent: 'head', pivot: [0, 12, 0], cubes: [{ o: [-4, 8, -3.5], s: [8, 8, 7], uv: [0, 31], inflate: 0.3 }] },
    { name: 'brim', parent: 'head', pivot: [0, 12.5, 0], cubes: [{ o: [-7, 12, -6], s: [14, 1, 12], uv: [0, 46] }] },
    { name: 'nose', parent: 'head', pivot: [0, 9, -4], cubes: [{ o: [-1, 8, -4.5], s: [2, 2, 1], uv: [23, 0] }] },
    { name: 'rightArm', pivot: [-3, 7.75, 0], cubes: [{ o: [-4, 2.75, -1], s: [2, 5, 2], uv: [24, 15] }] },
    { name: 'leftArm', pivot: [3, 7.75, 0], cubes: [{ o: [2, 2.75, -1], s: [2, 5, 2], uv: [16, 15] }] },
    { name: 'rightLeg', pivot: [-1, 2.5, 0], cubes: [{ o: [-2, 0, -1], s: [2, 3, 2], uv: [8, 23] }] },
    { name: 'leftLeg', pivot: [1, 2.5, 0], cubes: [{ o: [0, 0, -1], s: [2, 3, 2], uv: [0, 23] }] },
  ],
};

interface Built { root: THREE.Group; bones: Record<string, THREE.Group> }

/** Bedrock geometry -> pivot groups in vanilla-Java part space under `root`. */
function buildGeo(geo: Geo, mat: THREE.Material): Built {
  const root = new THREE.Group();
  const bones: Record<string, THREE.Group> = {};
  const pivots: Record<string, V3> = {};
  const rel = (c: GeoCube, p: V3): CubeDef => ({
    tex: c.uv, from: [c.o[0] - p[0], p[1] - c.o[1] - c.s[1], c.o[2] - p[2]], size: c.s, inflate: c.inflate, mirror: c.mirror,
  });
  for (const b of geo.bones) {
    const p = b.pivot;
    const pp = b.parent ? pivots[b.parent] : undefined;
    const plain = (b.cubes ?? []).filter((c) => !c.rot);
    const g = buildPart({
      cubes: plain.map((c) => rel(c, p)),
      pos: pp ? [p[0] - pp[0], pp[1] - p[1], p[2] - pp[2]] : [p[0], 24 - p[1], p[2]],
    }, geo.tw, geo.th, mat);
    for (const c of b.cubes ?? []) {
      if (!c.rot) continue;
      const cp = c.pivot ?? p;
      g.add(buildPart({
        cubes: [rel(c, cp)],
        pos: [cp[0] - p[0], p[1] - cp[1], cp[2] - p[2]],
        rot: [c.rot[0] * PI / 180, c.rot[1] * PI / 180, c.rot[2] * PI / 180],
      }, geo.tw, geo.th, mat));
    }
    (b.parent ? bones[b.parent] : root).add(g);
    bones[b.name] = g;
    pivots[b.name] = p;
  }
  return { root, bones };
}

/** Same shape as entityRenderer's Anim. */
export interface BabyAnim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

function make(env: EnvUniforms, sheet: string, geo: Geo, pose: (b: Record<string, THREE.Group>, v: MobView, q: QuadPose, age: number) => void,
  extra: (b: Built, mats: EntityMat[]) => void = () => {}): BabyAnim {
  const mat = entityMaterial(env, sheetTexture(sheet), { doubleSided: geo === CHICKEN });
  const mats = [mat];
  const built = buildGeo(geo, mat);
  extra(built, mats);
  return {
    parts: built.root, mats,
    animate(v, q, _hy, _hx, age) { pose(built.bones, v, q, age); },
    dispose() { for (const m of mats) m.dispose(); },
  };
}

function zombiePose(b: Record<string, THREE.Group>, v: MobView, q: QuadPose, age: number): void {
  b.head.rotation.set(q.headX, q.headY, 0);
  const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
  b.rightLeg.rotation.x = Math.cos(s) * a;
  b.leftLeg.rotation.x = Math.cos(s + PI) * a;
  zombieArms(b, v, age);
}

/** AnimationUtils.animateZombieArms */
function zombieArms(b: Record<string, THREE.Group>, v: MobView, age: number): void {
  const atk = Math.sin(v.attackAnim * PI);
  const atk2 = Math.sin((1 - (1 - v.attackAnim) * (1 - v.attackAnim)) * PI);
  const x = -PI / (v.aggressive ? 1.5 : 2.25) + atk * 1.2 - atk2 * 0.4;
  const bobZ = Math.cos(age * 0.09) * 0.05 + 0.05, bobX = Math.sin(age * 0.067) * 0.05;
  b.rightArm.rotation.set(x + bobX, -(0.1 - atk * 0.6), bobZ);
  b.leftArm.rotation.set(x - bobX, 0.1 - atk * 0.6, -bobZ);
}

function quadPose(b: Record<string, THREE.Group>, _v: MobView, q: QuadPose): void {
  b.head.rotation.set(q.headX, q.headY, 0);
  const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
  b.leg0.rotation.x = Math.cos(s) * a;
  b.leg1.rotation.x = Math.cos(s + PI) * a;
  b.leg2.rotation.x = Math.cos(s + PI) * a;
  b.leg3.rotation.x = Math.cos(s) * a;
}

function chickPose(b: Record<string, THREE.Group>, _v: MobView, q: QuadPose): void {
  // no separate head: the whole body looks around a little
  b.body.rotation.set(q.headX * 0.5, q.headY * 0.5, 0);
  const s = q.limbSwing * 0.6662, a = q.limbAmt * 1.4;
  b.leg0.rotation.x = Math.cos(s) * a;
  b.leg1.rotation.x = Math.cos(s + PI) * a;
  const flap = q.airborne ? (Math.sin(q.age * 1.1) + 1) * 0.9 : 0;
  b.wing0.rotation.z = flap;
  b.wing1.rotation.z = -flap;
}

/** Baby villager / zombie villager: the sheet is the biome-type layer stack (MobView.color & 7). */
function villagerBaby(env: EnvUniforms, zombie: boolean): BabyAnim {
  const tint = zombie ? 0x4f7a4a : 0x9c6b4a;
  const mat = entityMaterial(env, composedTexture(villagerLayers(zombie, true, 0, 0, 1), tint));
  const { root, bones: b } = buildGeo(zombie ? ZOMBIE_VILLAGER : VILLAGER, mat);
  let lastType = 0;
  return {
    parts: root, mats: [mat],
    animate(v, q, _hy, _hx, age) {
      const c = v.color | 0;
      if ((c & 7) !== lastType) {
        lastType = c & 7;
        mat.uniforms.uMap.value = composedTexture(villagerLayers(zombie, true, lastType, 0, 1), tint);
      }
      const asleep = (c & 1024) !== 0;
      // head shake (refusal): MobView.swell carries shake ticks / 20
      const shake = !zombie && v.swell > 0 ? Math.sin(v.swell * 20 * 1.2) * 0.45 : 0;
      b.head.rotation.set(asleep ? 0 : q.headX, asleep ? 0 : q.headY + shake, 0);
      const s = q.limbSwing * 0.6662, a = (asleep ? 0 : q.limbAmt) * 1.4 * 0.5;
      b.rightLeg.rotation.x = Math.cos(s) * a;
      b.leftLeg.rotation.x = Math.cos(s + PI) * a;
      if (zombie) zombieArms(b, v, age);
    },
    dispose() { mat.dispose(); },
  };
}

/** vanilla DyeColor texture colours (as SheepModel.COLORS) */
const WOOL = [
  0xf9fffe, 0xf9801d, 0xc74ebd, 0x3ab3da, 0xfed83d, 0x80c71f, 0xf38baa, 0x474f52,
  0x9d9d97, 0x169c9c, 0x8932b8, 0x3c44aa, 0x835432, 0x5e7c16, 0xb02e26, 0x1d1d21,
];

const BUILDERS: Partial<Record<MobType, (env: EnvUniforms) => BabyAnim>> = {
  zombie: (env) => make(env, 'entity/zombie/zombie_baby', ZOMBIE, zombiePose),
  husk: (env) => make(env, 'entity/zombie/husk_baby', ZOMBIE, zombiePose),
  drowned: (env) => make(env, 'entity/zombie/drowned_baby', ZOMBIE, zombiePose, (b, mats) => {
    // DrownedOuterLayer: the same model, slightly inflated, with the outer sheet
    const outer = entityMaterial(env, sheetTexture('entity/zombie/drowned_outer_layer_baby'));
    mats.push(outer);
    const o = buildGeo({ ...ZOMBIE, bones: ZOMBIE.bones.map((bn) => ({ ...bn, cubes: bn.cubes?.slice(0, 1).map((c) => ({ ...c, inflate: 0.15 })) })) }, outer);
    for (const name of Object.keys(o.bones)) {
      const mesh = o.bones[name].children.find((c) => (c as THREE.Mesh).isMesh);
      if (mesh) b.bones[name].add(mesh);
    }
  }),
  villager: (env) => villagerBaby(env, false),
  zombie_villager: (env) => villagerBaby(env, true),
  pig: (env) => make(env, 'entity/pig/pig_temperate_baby', PIG, quadPose),
  cow: (env) => make(env, 'entity/cow/cow_temperate_baby', COW, quadPose),
  chicken: (env) => make(env, 'entity/chicken/chicken_temperate_baby', CHICKEN, chickPose),
  sheep: (env) => {
    let wool: EntityMat | null = null;
    let woolMeshes: THREE.Object3D[] = [];
    const anim = make(env, 'entity/sheep/sheep_baby', SHEEP, (b, v, q) => {
      quadPose(b, v, q);
      // grazing: head dips and nods (SheepModel.prepareMobModel, half the adult drop)
      const t = q.eat ?? 0;
      const dip = t <= 0 ? 0 : t >= 4 && t <= 36 ? 1 : t < 4 ? t / 4 : -(t - 40) / 4;
      b.head.position.y = 24 - 8.5 + dip * 3;
      if (t > 4 && t <= 36) b.head.rotation.x = PI / 5 + 0.21991149 * Math.sin(((t - 4) / 32) * 28.7);
      else if (t > 0) b.head.rotation.x = PI / 5;
      wool!.uniforms.uTint.value.setHex(WOOL[q.color & 15] ?? 0xffffff);
      for (const m of woolMeshes) m.visible = !q.sheared;
    }, (b, mats) => {
      wool = entityMaterial(env, sheetTexture('entity/sheep/sheep_wool_baby'));
      mats.push(wool);
      const o = buildGeo({ ...SHEEP, bones: SHEEP.bones.map((bn) => ({ ...bn, cubes: bn.cubes?.map((c) => ({ ...c, inflate: 0.3 })) })) }, wool);
      for (const name of Object.keys(o.bones)) {
        const mesh = o.bones[name].children[0];
        b.bones[name].add(mesh);
        woolMeshes.push(mesh);
      }
    });
    return anim;
  },
};

/** Mob types whose babies have their own model. */
export function hasBabyModel(t: MobType): boolean { return t in BUILDERS; }

export function babyAnim(env: EnvUniforms, t: MobType): BabyAnim | null {
  return BUILDERS[t]?.(env) ?? null;
}
